import { Singleton } from "@/utils/Singleton";
import { FrontendConfigurationAuth0CacheLocationEnum } from "@/apiClientGenerated";

/**
 * @interface StaticConfiguration
 */
export interface StaticConfiguration {
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  PUSHER_KEY: string;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  PUSHER_CLUSTER: string;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  LOGIN_ROUTE?: string;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  HOME_ROUTE?: string;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  ONLY_OFFICE_URL: string;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  ONLY_OFFICE_COMMAND_SERVER: string;
  /**
   * * @type {number}
   * @memberof FrontendConfiguration
   */
  ONLY_OFFICE_FORCE_SAVE_INTERVAL?: number;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  AMPLITUDE_KEY: string;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  AMPLITUDE_SERVER_URL?: string;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  AUTH0_DOMAIN: string;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  AUTH0_CLIENT_ID: string;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  AUTH0_AUDIENCE: string;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  AUTH0_CACHE_LOCATION?: FrontendConfigurationAuth0CacheLocationEnum;
  /**
   * * @type {number}
   * @memberof FrontendConfiguration
   */
  FILE_UPLOAD_POOL_SIZE?: number;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  SENTRY_DSN?: string;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  SENTRY_ENVIRONMENT?: string;
  /**
   * * @type {number}
   * @memberof FrontendConfiguration
   */
  SENTRY_TRACE_SAMPLE_RATE?: number;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  SENTRY_RELEASE?: string;
  /**
   * * @type {string}
   * @memberof FrontendConfiguration
   */
  FROALA_LICENSE_KEY?: string;
  /**
   * * @type {boolean}
   * @memberof FrontendConfiguration
   */
  MAINTENANCE_MODE?: boolean;
  /**
   * * @type {number}
   * @memberof FrontendConfiguration
   */
  S3_UPLOAD_THRESHOLD?: number;
  /**
   * * @type {number}
   * @memberof FrontendConfiguration
   */
  RESET_TOKEN_ISSUED_BEFORE?: number;
}

let staticConfigurationPromise: Promise<StaticConfiguration> | null = null;

const defaultValues = {
  HOME_ROUTE: "news",
};

// see 20-generate-config.sh for more information
export async function getStaticConfiguration(): Promise<StaticConfiguration> {
  if (staticConfigurationPromise === null) {
    staticConfigurationPromise = fetch("/configuration.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load config: ${response.statusText}`);
        }

        return response.json();
      })
      .then((data) => Object.assign({}, defaultValues, data))
      .catch((error) => {
        staticConfigurationPromise = null;
        throw error;
      });
  }

  return staticConfigurationPromise;
}

/**
 * Use apiUrl instead. This function has been exported only to be imported in a test
 *
 * @internal
 * @param host string
 * @param protocol string
 */
export function resolveApiUrl(host: string, protocol = "https:"): string {
  const regexRootDomain = /^app\.(.*)$/;

  if (regexRootDomain.test(host)) {
    const apiHost = host.replace(regexRootDomain, "api.$1");

    return `${protocol}//${apiHost}`;
  }

  const regexes = [
    /^(.*)\.(preview-apps\.pillar\.science)$/,
    /^(.*)\.(kube\.pillar\.science)$/,
  ];

  for (const regex of regexes) {
    if (regex.test(host)) {
      return host.replace(regex, `${protocol}//$1-api.$2`);
    }
  }

  const regExp = /^(.*)\.(pillar\.science)$/;

  if (regExp.test(host)) {
    return host.replace(regExp, `${protocol}//$1.api.$2`);
  }

  return `${protocol}//localhost:8000`;
}

export const apiUrl = new Singleton(() =>
  resolveApiUrl(window.location.host, window.location.protocol)
);

export async function resolveCubejsUrl(
  useInternalImplementation: boolean
): Promise<string> {
  const config = await getStaticConfiguration();

  if (!config) {
    throw new Error("Could not resolve cubejs url for host");
  }

  return useInternalImplementation
    ? config.CUBEJS_INTERNAL_URL
    : config.CUBEJS_URL;
}

export const DEFAULT_DEBOUNCE = 200;
export const DEBOUNCE_MID = 600;
export const ACCOUNT_EXPIRATION_THRESHOLD_DAYS = 30;
